import NativeDateInput from 'components/common/field/NativeDateInput'
import Modal from 'components/common/modal/Modal'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayMode } from 'types/customParams'

export type InterestFormValues = {
  moveInDate: string
  message: string
  acceptCreditCheck: boolean
}

type InterestModalProps = {
  isOpen: boolean
  isLoading?: boolean
  onClose: () => void
  onSubmit: (values: InterestFormValues) => void | Promise<void>
}

const InterestModal = ({ isOpen, isLoading, onClose, onSubmit }: InterestModalProps) => {
  const { t } = useTranslation()
  const [moveInDate, setMoveInDate] = useState('')
  const [message, setMessage] = useState('')
  const [acceptCreditCheck, setAcceptCreditCheck] = useState(false)

  const canSubmit = !!moveInDate && acceptCreditCheck && !isLoading

  const handleSubmit = () => {
    if (!canSubmit) return
    void onSubmit({
      moveInDate,
      message: message.trim(),
      acceptCreditCheck,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={onClose}
      title={t('listing.interest.title')}
      subtitle={t('listing.interest.subtitle')}
      headerVariant="light"
      manualSize={DisplayMode.Modal}
      allowChangeDisplayMode={false}
      className="!h-auto !max-h-[90vh] !w-[min(92vw,28rem)] rounded-2xl"
      customBodyClasses="!bg-white"
    >
      <div className="flex flex-col gap-5 px-6 pb-6 pt-5 text-sm">
        <div className="flex flex-col gap-1.5">
          <label className="block text-sm font-semibold text-imovSecondary">
            {t('listing.interest.moveInDate')}
          </label>
          <NativeDateInput
            value={moveInDate}
            onChange={e => setMoveInDate(e.target.value)}
            className="date-input-native w-full cursor-pointer rounded-xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:border-imovPrimary focus:outline-none focus:ring-2 focus:ring-imovPrimary/50"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="block text-sm font-semibold text-imovSecondary">
            {t('listing.interest.message')}
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder={t('listing.interest.messagePlaceholder')}
            className="w-full resize-none rounded-xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:border-imovPrimary focus:outline-none focus:ring-2 focus:ring-imovPrimary/50"
          />
        </div>

        <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm leading-snug text-imovSecondary">
          <input
            type="checkbox"
            checked={acceptCreditCheck}
            onChange={e => setAcceptCreditCheck(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-imovPrimary"
          />
          <span>{t('listing.interest.creditCheck')}</span>
        </label>

        <div className="flex flex-col gap-3 pt-1">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="w-full rounded-full bg-imovPrimary py-3.5 text-sm font-semibold text-white transition-colors hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40 disabled:opacity-100"
          >
            {t('listing.interest.submit')}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="w-full rounded-lg py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default InterestModal
